import React, { useEffect, useState } from "react";
import { Bot, Download, FolderArchive, Loader2, MessageSquare, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { downloadPdf } from "../../../pages/AdminConversations";
import { ParticipantSessions } from "./ParticipantSessions";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const ParticipantTools = ({ participant }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [openTool, setOpenTool] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    setData(null); setError(false); setOpenTool(null);
    api.get(`/admin/participants/${participant.id}/tools`, { params: { _: Date.now() } })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [participant.id]);

  const download = async (tool) => {
    if (downloading) return;
    setDownloading(tool.id);
    const ok = await downloadPdf(`/admin/participants/${participant.id}/tools/${tool.id}/download`, `${participant.name || participant.email}_${tool.name}.pdf`);
    setDownloading(null);
    ok ? toast.success(`Downloaded ${participant.name || participant.email}_${tool.name}.pdf`) : toast.error("Failed to download PDF");
  };

  const downloadReport = async () => {
    if (downloading) return;
    setDownloading("report");
    const zipName = `${participant.name || participant.email}_Program Report.zip`;
    const ok = await downloadPdf(`/admin/participants/${participant.id}/program-report`, zipName);
    setDownloading(null);
    ok ? toast.success(`Downloaded ${zipName}`) : toast.error("Failed to download Program Report");
  };
  const report = data?.program_report;
  const reportCount = report?.included.length ?? 0;

  return <>
    <div className="px-5 py-4 border-b border-zinc-900 bg-zinc-950/60">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80 mb-1">participant</div>
          <h3 className="font-display text-xl font-black tracking-tight text-white" data-testid="participant-name">{participant.name || participant.email}</h3>
          <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5 text-zinc-500" />{participant.email}</span>
            {participant.business_name && <span className="text-zinc-500">· {participant.business_name}</span>}
          </div>
        </div>
        {report && <div className="shrink-0 flex flex-col items-start sm:items-end gap-1.5">
          <button data-testid="program-report-download" onClick={downloadReport} disabled={!!downloading || reportCount === 0}
            title={reportCount ? `Includes: ${report.included.join(", ")}` : "No Goal Architect / Business Model / Business Gaps conversations yet"}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-200 border border-zinc-700 hover:border-emerald-500/50 hover:text-emerald-300 px-3 py-2 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-700 disabled:hover:text-zinc-200">
            {downloading === "report" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderArchive className="w-3 h-3" />}
            Program Report
            <span className="ml-1 font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 normal-case tracking-normal" data-testid="program-report-count">{reportCount} of {report.total} GPTs</span>
          </button>
          {reportCount === 0 && <span className="text-[11px] text-zinc-500 sm:text-right max-w-[260px]" data-testid="program-report-empty-note">No Goal Architect / Business Model / Business Gaps conversations yet</span>}
        </div>}
      </div>
    </div>
    <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="participant-tools-list">
      {error && <p className="text-sm text-red-300" data-testid="participant-tools-error">Could not load this participant's GPTs.</p>}
      {!data && !error && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading GPTs...</div>}
      {data && data.tools.length === 0 && <p className="text-sm text-zinc-500" data-testid="participant-no-tools">This participant hasn't used any GPT yet.</p>}
      {data && data.tools.map(({ tool, session_count, message_count, last_activity }) => (
        <div key={tool.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 overflow-hidden" data-testid={`participant-tool-${tool.id}`}>
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-emerald-300" /></div>
            <button className="flex-1 min-w-0 text-left" data-testid={`participant-tool-toggle-${tool.id}`} onClick={() => setOpenTool(openTool === tool.id ? null : tool.id)}>
              <div className="text-sm font-semibold text-white truncate">{tool.name}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3" />{session_count} chat{session_count === 1 ? "" : "s"} · {message_count} messages · last {fmtDate(last_activity)}
              </div>
            </button>
            <button data-testid={`participant-tool-download-${tool.id}`} onClick={() => download(tool)} disabled={!!downloading}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-200 border border-zinc-700 hover:border-emerald-500/50 hover:text-emerald-300 px-3 py-2 rounded-md transition-colors disabled:opacity-40 shrink-0"
              title={`Download ${participant.name || participant.email}_${tool.name}.pdf`}>
              {downloading === tool.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
            </button>
          </div>
          {openTool === tool.id && <ParticipantSessions participant={participant} tool={tool} />}
        </div>
      ))}
    </div>
  </>;
};
